import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ShoppingCart, ChevronDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { NAV, MARKETPLACE } from "@/constants/testIds";
import logo from "@/assets/Weautomate logo-01.png";

const NavItem = ({ to, label, testid }) => (
  <NavLink
    to={to}
    data-testid={testid}
    className={({ isActive }) =>
      `text-sm tracking-wide transition-colors ${isActive ? "text-white" : "text-white/60 hover:text-white"}`
    }
  >
    {label}
  </NavLink>
);

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const nav = useNavigate();

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-[#0A0B10]/70 backdrop-blur-xl border-b border-white/10">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid={NAV.logo} className="flex items-center group">
          <img src={logo} alt="WeAutomate" className="h-9 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <NavItem to="/oem-odm" label="OEM / ODM" testid={NAV.oem} />
          <div
            className="relative"
            onMouseEnter={() => setSolutionsOpen(true)}
            onMouseLeave={() => setSolutionsOpen(false)}
          >
            <button className="text-sm text-white/60 hover:text-white flex items-center gap-1">
              Solutions <ChevronDown className="w-3 h-3" />
            </button>
            {solutionsOpen && (
              <div className="absolute top-full pt-2 -left-2">
                <div className="glass rounded-2xl p-2 min-w-[230px]">
                  <Link to="/home-automation" data-testid={NAV.homeAutomation}
                        className="block px-4 py-2.5 rounded-xl hover:bg-white/5 text-sm">
                    Home Automation
                    <div className="text-xs text-white/40">Luxury with a touch of Magic</div>
                  </Link>
                  <Link to="/industrial-automation" data-testid={NAV.industrial}
                        className="block px-4 py-2.5 rounded-xl hover:bg-white/5 text-sm">
                    Industrial Automation
                    <div className="text-xs text-white/40">BMS, energy, access, surveillance</div>
                  </Link>
                </div>
              </div>
            )}
          </div>
          <NavItem to="/marketplace" label="Marketplace" testid={NAV.marketplace} />
          <NavItem to="/platforms" label="Platforms" testid={NAV.platforms} />
          <NavItem to="/opportunities" label="Grow with us" testid={NAV.opportunities} />
          <NavItem to="/investors" label="Investors" testid={NAV.investors} />
          <NavItem to="/contact" label="Contact" testid={NAV.contact} />
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/cart" data-testid={MARKETPLACE.cartIcon}
                className="relative w-9 h-9 rounded-full border border-white/10 flex items-center justify-center hover:border-white/30">
            <ShoppingCart className="w-4 h-4" />
            {count > 0 && (
              <span data-testid={MARKETPLACE.cartCount}
                    className="absolute -top-1 -right-1 text-[10px] gradient-bg rounded-full w-4 h-4 flex items-center justify-center font-semibold">
                {count}
              </span>
            )}
          </Link>
          {user ? (
            <div className="hidden lg:flex items-center gap-2">
              <Link to="/dashboard" data-testid={NAV.dashboard}
                    className="text-sm px-3 py-1.5 rounded-full border border-white/10 hover:border-white/30">
                {user.name.split(" ")[0]}
              </Link>
              <button data-testid={NAV.logout} onClick={() => { logout(); nav("/"); }}
                      className="text-sm text-white/60 hover:text-white">Logout</button>
            </div>
          ) : (
            <div className="hidden lg:flex items-center gap-2">
              <Link to="/login" data-testid={NAV.login} className="text-sm text-white/70 hover:text-white">Login</Link>
              <Link to="/register" data-testid={NAV.register}
                    className="text-sm px-4 py-2 rounded-full gradient-bg font-medium hover:shadow-[0_0_20px_rgba(157,76,221,0.5)] transition-shadow">
                Get Started
              </Link>
            </div>
          )}
          <button data-testid={NAV.mobileMenu} className="lg:hidden p-2"
                  onClick={() => setOpen(!open)}>
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#0A0B10]">
          <div className="px-6 py-4 flex flex-col gap-3">
            <NavLink to="/oem-odm" onClick={() => setOpen(false)} className="text-sm">OEM / ODM</NavLink>
            <NavLink to="/home-automation" onClick={() => setOpen(false)} className="text-sm">Home Automation</NavLink>
            <NavLink to="/industrial-automation" onClick={() => setOpen(false)} className="text-sm">Industrial Automation</NavLink>
            <NavLink to="/marketplace" onClick={() => setOpen(false)} className="text-sm">Marketplace</NavLink>
            <NavLink to="/platforms" onClick={() => setOpen(false)} className="text-sm">Platforms</NavLink>
            <NavLink to="/opportunities" onClick={() => setOpen(false)} className="text-sm">Grow with us</NavLink>
            <NavLink to="/investors" onClick={() => setOpen(false)} className="text-sm">Investors</NavLink>
            <NavLink to="/contact" onClick={() => setOpen(false)} className="text-sm">Contact</NavLink>
            {!user && (
              <div className="flex gap-3 pt-2">
                <Link to="/login" onClick={() => setOpen(false)} className="text-sm px-4 py-2 rounded-full border border-white/10">Login</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="text-sm px-4 py-2 rounded-full gradient-bg">Get Started</Link>
              </div>
            )}
            {user && (
              <Link to="/dashboard" onClick={() => setOpen(false)} className="text-sm px-4 py-2 rounded-full gradient-bg w-fit">Dashboard</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
